// Module 16(b) — the Source step's platform catalog and its connected-account lists.
//
// ONE table replaces two legacy surfaces: `DataExport.vue:67-102` (`getAccounts`, which hand-mapped
// integration keys into eight sibling refs on the page host) and `DataExportCreate.vue:267-313`
// (`platformOptions`, which paired each of those refs with the matching `export_hub_platforms`
// entitlement). Nothing here fetches — module 12 already holds the workspace's linked accounts, so
// this is a pure read over `useIntegrations()`.
//
// THE CRITEO FIX (ORP-18754). `criteoAccounts` was declared (`DataExport.vue:62`) and passed down
// (`:29`), but `getAccounts()` mapped only seven integrations and never wrote it. It stayed `[]`
// forever, so the gate `props.criteoAccounts?.length > 0` (`DataExportCreate.vue:308`) could not
// pass, the Criteo checkbox never rendered, and the Criteo picker, its watcher and the
// `criteo_account` partition were all dead in production. There is no per-platform mapping left to
// forget: every platform, Criteo included, resolves through the same descriptor row below.
import { computed, type ComputedRef } from 'vue'

import type { ExportPlatform } from '../models/exportJob.types'

import { useRbac, useWorkspace } from '@/features/account'
import { useIntegrations, type IntegrationAccountDto } from '@/features/integrations'

/** The entitlement carrying the exportable platform ids. A scope-list permission, not a flag. */
export const PLATFORMS_PERMISSION = 'export_hub_platforms'

/**
 * `DataExport.vue:94` — legacy exposed Google Analytics 4 as an export source to ONE workspace and
 * handed every other workspace an empty list.
 *
 * KEPT, named rather than dropped (ORP-18756 allows a magic id that is de-magicked and recorded):
 * removing it would newly open GA4 export to every entitled workspace, which is a product decision
 * a port does not get to make. Compared numerically because a workspace id reaches the client as
 * both a number and a string — legacy's `===` against a string silently disabled GA4 outright.
 */
export const GA4_EXPORT_WORKSPACE_ID = 10000993

export interface ExportPlatformDescriptor {
  readonly platform: ExportPlatform
  /** Module 12 channel alias; `accountsFor()` resolves it to the workspace-integration key. */
  readonly channel: string
  readonly labelKey: string
  /** `integration_config` field holding the human name (`DataExport.vue:67-101`). */
  readonly accountLabelField: string
  /** Google Ads is capped at one pick — its manager stage needs a single account (`Step1.vue:98`). */
  readonly maxSelection?: number
}

/** The eight platforms legacy offered, in `platformOptions`' order (`DataExportCreate.vue:269-311`). */
export const EXPORT_PLATFORMS: readonly ExportPlatformDescriptor[] = Object.freeze([
  {
    platform: 'meta_ads',
    channel: 'meta',
    labelKey: 'dataExport.source.platforms.metaAds',
    accountLabelField: 'name',
  },
  {
    platform: 'google_ads',
    channel: 'google',
    labelKey: 'dataExport.source.platforms.googleAds',
    accountLabelField: 'label',
    maxSelection: 1,
  },
  {
    platform: 'tiktok_ads',
    channel: 'tiktok',
    labelKey: 'dataExport.source.platforms.tiktokAds',
    accountLabelField: 'advertiser_name',
  },
  {
    platform: 'google_analytics',
    channel: 'ga4',
    labelKey: 'dataExport.source.platforms.googleAnalytics',
    accountLabelField: 'label',
  },
  {
    platform: 'appsflyer',
    channel: 'appsflyer',
    labelKey: 'dataExport.source.platforms.appsflyer',
    accountLabelField: 'label',
  },
  {
    platform: 'adjust',
    channel: 'adjust',
    labelKey: 'dataExport.source.platforms.adjust',
    accountLabelField: 'app_name',
  },
  {
    platform: 'google_search_console',
    channel: 'search_console',
    labelKey: 'dataExport.source.platforms.googleSearchConsole',
    accountLabelField: 'url',
  },
  {
    platform: 'criteo',
    channel: 'criteo',
    labelKey: 'dataExport.source.platforms.criteo',
    accountLabelField: 'advertiser_name',
  },
])

export interface PlatformAccountOption {
  id: number | string
  label: string
  /** Google Ads MCC row — picking it opens the second-stage "Ads Account" picker. */
  manager: boolean
  /** Google Ads: the manager id this row hangs off (`Step1.vue:350`). */
  customerId: number | string | null
}

export interface UsePlatformAccounts {
  /** Module 12's bootstrap section has answered. No retry seam: the shell owns that fetch. */
  isReady: ComputedRef<boolean>
  /** `export_hub_platforms` ∩ "has at least one connected account in this workspace". */
  availablePlatforms: ComputedRef<readonly ExportPlatformDescriptor[]>
  accountsFor: (platform: ExportPlatform) => PlatformAccountOption[]
  /** The accounts hanging off a Google Ads manager — the second stage of the MCC picker. */
  subAccountsOf: (platform: ExportPlatform, managerId: number | string) => PlatformAccountOption[]
}

function text(value: unknown): string | undefined {
  if (typeof value === 'number') return String(value)
  return typeof value === 'string' && value.trim() !== '' ? value : undefined
}

function identifier(value: unknown): number | string | null {
  return typeof value === 'number' || typeof value === 'string' ? value : null
}

/**
 * `manager` and `customerId` sit at the ROOT of the linked-account row, not inside
 * `integration_config` — legacy read them off the spread account object (`Step1.vue:348-350`).
 */
function toOption(
  account: IntegrationAccountDto,
  descriptor: ExportPlatformDescriptor,
): PlatformAccountOption {
  const config = (account.integration_config ?? {}) as Record<string, unknown>
  return {
    id: account.id,
    label:
      text(config[descriptor.accountLabelField]) ??
      text(config['label']) ??
      text(config['name']) ??
      String(account.id),
    manager: account['manager'] === true,
    customerId: identifier(account['customerId']),
  }
}

export function usePlatformAccounts(): UsePlatformAccounts {
  const { permissions } = useRbac()
  const { workspaceId } = useWorkspace()
  const integrations = useIntegrations()

  const entitled = computed<readonly string[]>(() => {
    const value = permissions.value[PLATFORMS_PERMISSION]
    return Array.isArray(value) ? value : []
  })

  const ga4Allowed = computed(() => Number(workspaceId.value) === GA4_EXPORT_WORKSPACE_ID)

  function options(descriptor: ExportPlatformDescriptor): PlatformAccountOption[] {
    if (descriptor.platform === 'google_analytics' && !ga4Allowed.value) return []
    return integrations
      .accountsFor(descriptor.channel)
      .map((account) => toOption(account, descriptor))
  }

  function accountsFor(platform: ExportPlatform): PlatformAccountOption[] {
    const descriptor = EXPORT_PLATFORMS.find((entry) => entry.platform === platform)
    return descriptor ? options(descriptor) : []
  }

  return {
    isReady: integrations.isReady,
    availablePlatforms: computed(() =>
      EXPORT_PLATFORMS.filter(
        (descriptor) =>
          entitled.value.includes(descriptor.platform) && options(descriptor).length > 0,
      ),
    ),
    accountsFor,
    subAccountsOf: (platform, managerId) =>
      accountsFor(platform).filter(
        (account) =>
          account.customerId !== null && Number(account.customerId) === Number(managerId),
      ),
  }
}
